/**
 * Which gym, and what it costs (B-50, D-103..D-105).
 *
 * The id rule is here rather than in the dashboard because `firestore.rules` checks the
 * same shape and the migration script builds the same ids: one definition, three callers
 * (D-09). The arithmetic is here because a figure shown to a person should be computable
 * in a unit test with no Firebase at all.
 */
/**
 * A PIN code is 4 to 10 digits. India's are six, which is the only country the SMS
 * allowlist currently admits (C-20), but hard-coding six would turn widening that
 * allowlist by one country into a sign-up that cannot be completed.
 */
const PIN_CODE = /^\d{4,10}$/;
export const GYM_NAME_MIN = 2;
export const GYM_NAME_MAX = 60;
const SLUG_MAX = 50;
export const COST_BOUNDS = {
    amountInr: { min: 1, max: 1_000_000 },
    months: { min: 1, max: 24 },
    sessions: { min: 1, max: 200 },
};
export function isPinCode(pin) {
    return PIN_CODE.test(pin);
}
/**
 * "Gold's Gym" → "golds-gym". Apostrophes vanish rather than splitting a word — the
 * ASCII one and the typographic ones iOS types by default (U+2018, U+2019, U+02BC),
 * written as escapes because the literal characters were once lost to an ASCII copy; every
 * other run of anything that is not a-z or 0-9 becomes one hyphen. A name written
 * entirely in another script slugs to "", which `gymId` refuses — recorded in C-27.
 */
export function gymSlug(name) {
    return name
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/['\u2018\u2019\u02BC]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, SLUG_MAX)
        .replace(/-+$/, "");
}
/**
 * The id is what the gym is, not when it was added (the B-34 lesson): the same name at
 * the same PIN is the same document, so a second person adding it finds the first.
 */
export function gymId(name, pinCode) {
    const trimmedName = name.trim();
    const pin = pinCode.trim();
    if (!isPinCode(pin))
        return null;
    if (trimmedName.length < GYM_NAME_MIN || trimmedName.length > GYM_NAME_MAX)
        return null;
    const slug = gymSlug(trimmedName);
    return slug ? `${pin}-${slug}` : null;
}
function whole(value, bounds) {
    return typeof value === "number" && Number.isInteger(value) && value >= bounds.min && value <= bounds.max;
}
function isRecord(value) {
    return typeof value === "object" && value !== null;
}
export function isValidGymCosts(value) {
    if (!isRecord(value) || !isRecord(value.membership))
        return false;
    const m = value.membership;
    if (!whole(m.amountInr, COST_BOUNDS.amountInr) || !whole(m.months, COST_BOUNDS.months))
        return false;
    if (value.pt === undefined)
        return true;
    if (!isRecord(value.pt))
        return false;
    const pt = value.pt;
    return (whole(pt.amountInr, COST_BOUNDS.amountInr) &&
        whole(pt.sessions, COST_BOUNDS.sessions) &&
        whole(pt.months, COST_BOUNDS.months));
}
/** Unrounded, so a figure divided further is rounded once and not twice. */
function monthlyExact(costs) {
    const membership = costs.membership.amountInr / costs.membership.months;
    const pt = costs.pt ? costs.pt.amountInr / costs.pt.months : 0;
    return membership + pt;
}
export function monthlySpend(costs) {
    return Math.round(monthlyExact(costs));
}
export function ptSessionCost(costs) {
    return costs.pt ? Math.round(costs.pt.amountInr / costs.pt.sessions) : null;
}
/**
 * A day that happened: at least one set, or marked as PT (D-107). A day with neither is
 * one someone opened and left, and must not lower the average (D-105).
 */
export function isGymDay(workout) {
    return workout.pt === true || workout.exercises.some((e) => e.sets.length > 0);
}
/**
 * What a day costs this calendar month, split by day type (D-106).
 *
 * Membership is spread over every gym day in `today`'s month; PT's monthly cost only over
 * the days marked PT, so a skipped session makes each attended one dearer and the two
 * figures add back up to what was actually spent. Each figure is rounded once, from
 * unrounded parts.
 */
export function sessionCost(costs, workouts, today) {
    const month = today.slice(0, 7);
    const days = workouts.filter((w) => w.date.slice(0, 7) === month && isGymDay(w));
    const sessions = days.length;
    const ptSessions = days.filter((w) => w.pt === true).length;
    const membership = costs.membership.amountInr / costs.membership.months;
    const pt = costs.pt ? costs.pt.amountInr / costs.pt.months : null;
    const normal = sessions > 0 ? membership / sessions : null;
    return {
        spend: Math.round(monthlyExact(costs)),
        sessions,
        ptSessions,
        perSession: normal === null ? null : Math.round(normal),
        perPtSession: normal !== null && pt !== null && ptSessions > 0 ? Math.round(normal + pt / ptSessions) : null,
    };
}
//# sourceMappingURL=gym.js.map